import React from 'react'
import type { BillEntryData } from './BillEntry'
import Edit from '../../images/icons/Edit'
import Delete from '../../images/icons/Delete'
import { formatNumberWithCommas } from './Helpers'
import { CircleAlert } from 'lucide-react'

interface MiniEntryProps {
  index: number
  entry: BillEntryData
  onEdit?: () => void
  onDelete?: () => void
}

const MiniEntry = ({ index, entry, onEdit, onDelete }: MiniEntryProps): React.ReactElement => {
  return (
    <div
      data-testid={`bill-upload-entry-${index}`}
      className='strata:flex strata:gap-x-4 strata:justify-between strata:items-center strata:rounded-sm'
    >
      <div className='strata:flex strata:flex-col strata:items-start strata:w-full strata:gap-y-1'>
        <span className='strata:text-body-sm strata:font-bold'>{entry.fileName ?? 'Bill'}</span>
        {entry.error ? (
          <p className='strata:text-body-xs strata:text-system-negative-foreground strata:flex strata:items-center strata:gap-1'>
            <CircleAlert className='strata:h-4 strata:w-4 strata:shrink-0' />
            {entry.error}
          </p>
        ) : (
          <div className='strata:flex strata:text-gray-600 strata:md:flex-wrap strata:md:flex-row strata:flex-col strata:w-full strata:items-start strata:gap-y-1 strata:md:gap-x-6'>
            <div className='strata:text-body-xs strata:md:basis-full strata:md:w-full strata:md:text-left'>
              <b>Address:</b>
              <span> {entry.address ?? 'N/A'}</span>
            </div>
            <div className='strata:flex strata:w-full strata:md:w-[unset] strata:items-start strata:md:gap-x-6'>
              <div className='strata:text-body-xs strata:w-1/2 strata:md:w-[unset] strata:text-left'>
                <b>Usage:</b>{' '}
                <span>
                  {entry?.usage != null ? `${formatNumberWithCommas(entry?.usage)} kWh` : 'N/A'}
                </span>
              </div>
              <div className='strata:text-body-xs strata:w-1/2 strata:md:w-[unset] strata:text-left'>
                <b>Bill:</b>{' '}
                <span>
                  {entry?.billing != null ? `$${formatNumberWithCommas(entry?.billing)}` : 'N/A'}
                </span>
              </div>
            </div>
            <div className='strata:flex strata:w-full strata:md:w-[unset] strata:items-start strata:md:gap-x-6'>
              <div className='strata:text-body-xs strata:w-1/2 strata:md:w-[unset] strata:text-left'>
                <b>Month:</b> <span>{entry?.month || 'N/A'}</span>
              </div>
              <div className='strata:text-body-xs strata:w-1/2 strata:md:w-[unset] strata:text-left'>
                <b>Year:</b> <span>{entry?.year ?? 'N/A'}</span>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className='strata:flex strata:justify-center strata:gap-3'>
        {!entry.error && (
          <button
            type='button'
            className='strata:h-5 strata:w-5 strata:cursor-pointer'
            onClick={onEdit}
            data-testid={`edit-button-${index}`}
          >
            <Edit className='strata:m-auto strata:h-5 strata:w-5' />
          </button>
        )}
        <button
          type='button'
          className='strata:h-5 strata:w-5 strata:cursor-pointer'
          onClick={onDelete}
          data-testid={`delete-button-${index}`}
        >
          <Delete className='strata:m-auto strata:h-5 strata:w-5 strata:py-0.5 strata:px-0.75' />
        </button>
      </div>
    </div>
  )
}

export default MiniEntry
